import { createContext, ReactNode, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getSession } from '@/api/bff';
import { ErrorScreen } from '@/components/ErrorScreen/ErrorScreen';
import { FullScreenLoader } from '@/components/LoadScreen/FullScreenLoader';
import { NoActiveRequestsError } from '@/pages/TransactionError/NoActiveRequestsError';
import { ErrorType, getErrorType } from '@/utils/errors';

export type PublicDataType = {
  cardLast4Array?: string[];
  displayTitle?: Record<string, string>;
  displayMessage?: Record<string, string>;
  puidArray?: string[];
  purchaseAmount?: string;
  merchantName?: string;
  cardProductMap?: Record<string, string>;
  [key: string]: unknown;
};

export type TransactionContextType = {
  nonce?: string;
  publicData: PublicDataType;
};

export const TransactionContext = createContext<
  TransactionContextType | undefined
>(undefined);

/**
 * A provider component that fetches and provides transaction data
 */
export function TransactionProvider({ children }: { children: ReactNode }) {
  const { intentOwner, workflow, sessionId } = useParams();
  const [transaction, setTransaction] = useState<TransactionContextType>();
  const [loading, setLoading] = useState(true);
  const [errorType, setErrorType] = useState<ErrorType | null>(null);

  useEffect(() => {
    async function fetchSessionData() {
      if (!intentOwner || !workflow || !sessionId) {
        setErrorType('generic');
        return;
      }
      try {
        const sessionData = await getSession(intentOwner, workflow, sessionId);

        const dynamicFields = sessionData.publicData.reduce(
          (
            acc: PublicDataType,
            item: { fieldName: string; fieldValue: string },
          ) => {
            try {
              acc[item.fieldName] = JSON.parse(item.fieldValue);
            } catch {
              acc[item.fieldName] = item.fieldValue;
            }
            return acc;
          },
          {},
        );

        const transactionData: TransactionContextType = {
          nonce: sessionData.nonce,
          publicData: dynamicFields,
        };

        setTransaction(transactionData);
      } catch (error) {
        setErrorType(getErrorType(error));
      } finally {
        setLoading(false);
      }
    }
    fetchSessionData();
  }, [intentOwner, workflow, sessionId]);

  if (errorType) {
    switch (errorType) {
      case 'noActiveRequests':
        return <NoActiveRequestsError />;
      default:
        return <ErrorScreen />;
    }
  }

  if (loading) {
    return <FullScreenLoader />;
  }

  return (
    <TransactionContext.Provider value={transaction}>
      {children}
    </TransactionContext.Provider>
  );
}
